/**
 * Enterprise Training Module Component
 * Security Awareness Training Management
 */

import React, { useState, useEffect } from 'react';
import { useI18n } from '../../context/I18nContext';
import { trainingService } from '../../services/enterpriseService';
import { grcService } from '../../services/grcService';
import { Modal, ControlSelector, useEvidenceIntegration } from './SharedModals';
import {
  GraduationCap, Users, Clock, CheckCircle, XCircle, AlertTriangle,
  Plus, Play, Award, BookOpen, Calendar, TrendingUp, RefreshCw,
  Search, Filter, ChevronRight, BarChart2, UserCheck, Mail, X, Save,
  Link2, Shield, FileText, Video, HelpCircle
} from 'lucide-react';

interface Course {
  id: number;
  title: string;
  description?: string;
  course_type: string;
  duration_minutes: number;
  passing_score: number;
  is_mandatory: boolean;
  is_active: boolean;
  created_at: string;
}

interface Assignment {
  id: number;
  course_id: number;
  user_id: number;
  status: 'assigned' | 'in_progress' | 'completed' | 'overdue';
  assigned_at: string;
  due_date?: string;
  completed_at?: string;
  score?: number;
  course_title?: string;
  user_name?: string;
}

interface TrainingStats {
  total_courses: number;
  total_assignments: number;
  completed: number;
  in_progress: number;
  overdue: number;
  completion_rate: number;
}

export const EnterpriseTraining: React.FC = () => {
  const { language } = useI18n();
  const [courses, setCourses] = useState<Course[]>([]);
  const [stats, setStats] = useState<TrainingStats | null>(null);
  const [overdueAssignments, setOverdueAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'courses' | 'assignments' | 'reports'>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showNewCourseModal, setShowNewCourseModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showLinkControlsModal, setShowLinkControlsModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [saving, setSaving] = useState(false);
  
  // New course form
  const [newCourse, setNewCourse] = useState({
    title: '',
    description: '',
    course_type: 'security_awareness',
    duration_minutes: 30,
    passing_score: 80,
    is_mandatory: false,
    content_type: 'video'
  });
  
  // Framework/control linking
  const [frameworks, setFrameworks] = useState<any[]>([]);
  const [selectedFramework, setSelectedFramework] = useState<number>(0);
  const [selectedControls, setSelectedControls] = useState<number[]>([]);
  
  const { linkTrainingToControl } = useEvidenceIntegration();

  useEffect(() => {
    loadData();
    loadFrameworks();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [coursesData, summaryData, overdueData] = await Promise.all([
        trainingService.getCourses(),
        trainingService.getComplianceSummary().catch(() => null),
        trainingService.getOverdue().catch(() => ({ overdue: [] }))
      ]);
      
      // Handle API response format - may be { data: [...] }, { courses: [...] }, or direct array
      const coursesList = coursesData?.data || coursesData?.courses || (Array.isArray(coursesData) ? coursesData : []);
      setCourses(coursesList);
      setStats(summaryData?.data || summaryData);
      const overdueList = overdueData?.data || overdueData?.overdue || (Array.isArray(overdueData) ? overdueData : []);
      setOverdueAssignments(overdueList);
    } catch (error) {
      console.error('Error loading training data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadFrameworks = async () => {
    try {
      const fws = await grcService.getFrameworks();
      setFrameworks(fws);
      if (fws.length > 0) setSelectedFramework(fws[0].id);
    } catch (error) {
      console.error('Error loading frameworks:', error);
    }
  };
  
  const handleCreateCourse = async () => {
    setSaving(true);
    try {
      const result = await trainingService.createCourse(newCourse);
      if (result) {
        await loadData();
        setShowNewCourseModal(false);
        setNewCourse({
          title: '',
          description: '',
          course_type: 'security_awareness',
          duration_minutes: 30,
          passing_score: 80,
          is_mandatory: false,
          content_type: 'video'
        });
      }
    } catch (error) {
      console.error('Error creating course:', error);
    } finally {
      setSaving(false);
    }
  };
  
  const handleLinkToControls = async () => {
    if (!selectedCourse || selectedControls.length === 0) return;
    setSaving(true);
    try {
      // Link training course to selected controls as evidence
      for (const controlId of selectedControls) {
        await linkTrainingToControl(
          controlId,
          selectedCourse.id,
          selectedCourse.title,
          stats?.completion_rate || 0
        );
      }
      setShowLinkControlsModal(false);
      setSelectedControls([]);
    } catch (error) {
      console.error('Error linking to controls:', error);
    } finally {
      setSaving(false);
    }
  };
  
  const openLinkModal = (course: Course) => {
    setSelectedCourse(course);
    setShowLinkControlsModal(true);
  };

  const courseTypes = {
    security_awareness: { label: language === 'sr' ? 'Svjesnost o sigurnosti' : 'Security Awareness', color: 'blue' },
    compliance: { label: language === 'sr' ? 'Usklađenost' : 'Compliance', color: 'purple' },
    onboarding: { label: language === 'sr' ? 'Onboarding' : 'Onboarding', color: 'green' },
    role_specific: { label: language === 'sr' ? 'Specifično za ulogu' : 'Role Specific', color: 'orange' },
    custom: { label: language === 'sr' ? 'Prilagođeno' : 'Custom', color: 'gray' }
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
      gray: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    };
    const typeInfo = courseTypes[type as keyof typeof courseTypes];
    return colors[typeInfo?.color || 'gray'];
  };

  const filteredCourses = courses.filter(course =>
    course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    course.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <GraduationCap className="w-7 h-7 text-indigo-500" />
            {language === 'sr' ? 'Obuka o sigurnosti' : 'Security Awareness Training'}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {language === 'sr' 
              ? 'Upravljajte obukama zaposlenika i pratite napredak'
              : 'Manage employee training and track progress'
            }
          </p>
        </div>
        <button 
          onClick={() => setShowNewCourseModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {language === 'sr' ? 'Novi kurs' : 'New Course'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {[
          { key: 'overview', icon: BarChart2, label: language === 'sr' ? 'Pregled' : 'Overview' },
          { key: 'courses', icon: BookOpen, label: language === 'sr' ? 'Kursevi' : 'Courses' },
          { key: 'assignments', icon: UserCheck, label: language === 'sr' ? 'Dodijeljeno' : 'Assignments' },
          { key: 'reports', icon: TrendingUp, label: language === 'sr' ? 'Izvještaji' : 'Reports' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as typeof activeTab)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center justify-between mb-3">
                <BookOpen className="w-8 h-8 text-indigo-500" />
                <span className="text-xs text-gray-500">{language === 'sr' ? 'Ukupno' : 'Total'}</span>
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-white">{courses.length}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Aktivnih kurseva' : 'Active Courses'}</div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center justify-between mb-3">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
                <span className="text-xs text-emerald-500">+12%</span>
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats?.completion_rate || 0}%</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Stopa završetka' : 'Completion Rate'}</div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center justify-between mb-3">
                <Clock className="w-8 h-8 text-blue-500" />
                <span className="text-xs text-gray-500">{language === 'sr' ? 'U toku' : 'In Progress'}</span>
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats?.in_progress || 0}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Aktivne obuke' : 'Active Trainings'}</div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center justify-between mb-3">
                <AlertTriangle className="w-8 h-8 text-red-500" />
                <span className="text-xs text-red-500">{language === 'sr' ? 'Hitno' : 'Urgent'}</span>
              </div>
              <div className="text-3xl font-bold text-gray-900 dark:text-white">{overdueAssignments.length}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Prekoračeno' : 'Overdue'}</div>
            </div>
          </div>

          {/* Overdue Alerts */}
          {overdueAssignments.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800 p-5">
              <h3 className="text-lg font-semibold text-red-700 dark:text-red-300 flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5" />
                {language === 'sr' ? 'Prekoračene obuke' : 'Overdue Training'}
              </h3>
              <div className="space-y-2">
                {overdueAssignments.slice(0, 5).map((assignment, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg p-3">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{assignment.course_title || `Course #${assignment.course_id}`}</div>
                      <div className="text-sm text-gray-500">{assignment.user_name || `User #${assignment.user_id}`}</div>
                    </div>
                    <button className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm hover:bg-red-200">
                      <Mail className="w-4 h-4" />
                      {language === 'sr' ? 'Podsjeti' : 'Remind'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Courses */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {language === 'sr' ? 'Nedavni kursevi' : 'Recent Courses'}
            </h3>
            <div className="space-y-3">
              {courses.slice(0, 5).map(course => (
                <div key={course.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{course.title}</div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className={`px-2 py-0.5 rounded ${getTypeColor(course.course_type)}`}>
                          {courseTypes[course.course_type as keyof typeof courseTypes]?.label || course.course_type}
                        </span>
                        <span>{course.duration_minutes} min</span>
                        {course.is_mandatory && (
                          <span className="text-red-500">{language === 'sr' ? 'Obavezno' : 'Required'}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Courses Tab */}
      {activeTab === 'courses' && (
        <div className="space-y-4">
          {/* Search */}
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={language === 'sr' ? 'Pretraži kurseve...' : 'Search courses...'}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
              <Filter className="w-4 h-4" />
              {language === 'sr' ? 'Filter' : 'Filter'}
            </button>
          </div>

          {/* Course Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCourses.map(course => (
              <div key={course.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 hover:shadow-lg transition-all cursor-pointer">
                <div className="flex items-start justify-between mb-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(course.course_type)}`}>
                    {courseTypes[course.course_type as keyof typeof courseTypes]?.label || course.course_type}
                  </span>
                  {course.is_mandatory && (
                    <span className="px-2 py-1 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded text-xs">
                      {language === 'sr' ? 'Obavezno' : 'Required'}
                    </span>
                  )}
                </div>
                
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">{course.title}</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-4">{course.description}</p>
                
                <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {course.duration_minutes}m
                    </span>
                    <span className="flex items-center gap-1">
                      <Award className="w-4 h-4" />
                      {course.passing_score}%
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => openLinkModal(course)}
                      className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded"
                      title={language === 'sr' ? 'Poveži sa kontrolama' : 'Link to Controls'}
                    >
                      <Link2 className="w-4 h-4" />
                    </button>
                    <button className="flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700">
                      <Play className="w-4 h-4" />
                      {language === 'sr' ? 'Dodijeli' : 'Assign'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Assignments Tab */}
      {activeTab === 'assignments' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">
            {language === 'sr' ? 'Pregled dodjeljenih obuka - dolazi uskoro' : 'Assignment management - coming soon'}
          </p>
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">
            {language === 'sr' ? 'Izvještaji o obukama - dolazi uskoro' : 'Training reports - coming soon'}
          </p>
        </div>
      )}
      
      {/* New Course Modal */}
      <Modal
        isOpen={showNewCourseModal}
        onClose={() => setShowNewCourseModal(false)}
        title={language === 'sr' ? 'Novi kurs obuke' : 'New Training Course'}
        onSave={handleCreateCourse}
        saveLabel={language === 'sr' ? 'Kreiraj kurs' : 'Create Course'}
        saving={saving}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Naziv kursa' : 'Course Title'} *
            </label>
            <input
              type="text"
              value={newCourse.title}
              onChange={(e) => setNewCourse({...newCourse, title: e.target.value})}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              placeholder={language === 'sr' ? 'npr. Sigurnost lozinki' : 'e.g. Password Security'}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Opis' : 'Description'}
            </label>
            <textarea
              value={newCourse.description}
              onChange={(e) => setNewCourse({...newCourse, description: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {language === 'sr' ? 'Tip kursa' : 'Course Type'}
              </label>
              <select
                value={newCourse.course_type}
                onChange={(e) => setNewCourse({...newCourse, course_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="security_awareness">Security Awareness</option>
                <option value="compliance">Compliance</option>
                <option value="onboarding">Onboarding</option>
                <option value="role_specific">Role Specific</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {language === 'sr' ? 'Tip sadržaja' : 'Content Type'}
              </label>
              <select
                value={newCourse.content_type}
                onChange={(e) => setNewCourse({...newCourse, content_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="video">Video</option>
                <option value="document">Document</option>
                <option value="quiz">Quiz</option>
                <option value="interactive">Interactive</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {language === 'sr' ? 'Trajanje (min)' : 'Duration (min)'}
              </label>
              <input
                type="number"
                value={newCourse.duration_minutes}
                onChange={(e) => setNewCourse({...newCourse, duration_minutes: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {language === 'sr' ? 'Prolazni rezultat (%)' : 'Passing Score (%)'}
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={newCourse.passing_score}
                onChange={(e) => setNewCourse({...newCourse, passing_score: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={newCourse.is_mandatory}
              onChange={(e) => setNewCourse({...newCourse, is_mandatory: e.target.checked})}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {language === 'sr' ? 'Obavezna obuka' : 'Mandatory Training'}
            </span>
          </label>
        </div>
      </Modal>
      
      {/* Link to Controls Modal */}
      <Modal
        isOpen={showLinkControlsModal}
        onClose={() => setShowLinkControlsModal(false)}
        title={language === 'sr' ? 'Poveži sa kontrolama' : 'Link to Controls'}
        onSave={handleLinkToControls}
        saveLabel={language === 'sr' ? 'Poveži kao dokaz' : 'Link as Evidence'}
        saving={saving}
        size="lg"
      >
        <div className="space-y-4">
          {selectedCourse && (
            <div className="p-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg">
              <p className="text-sm text-indigo-700 dark:text-indigo-300">
                <strong>{selectedCourse.title}</strong>
              </p>
              <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                {language === 'sr' 
                  ? 'Završetak ove obuke će se dodati kao dokaz za odabrane kontrole'
                  : 'Completion of this training will be added as evidence for selected controls'}
              </p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Odaberi okvir' : 'Select Framework'}
            </label>
            <select
              value={selectedFramework}
              onChange={(e) => {
                setSelectedFramework(Number(e.target.value));
                setSelectedControls([]);
              }}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              {frameworks.map(fw => (
                <option key={fw.id} value={fw.id}>{fw.name}</option>
              ))}
            </select>
          </div>
          
          {selectedFramework > 0 && (
            <ControlSelector
              frameworkId={selectedFramework}
              selectedControls={selectedControls}
              onSelectionChange={setSelectedControls}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default EnterpriseTraining;
